import {
  HHAlert,
  HHButton,
  HHStack,
  HHTooltip,
  HHTypography,
} from '@hinge-health/react-component-library';
import AddIcon from '@mui/icons-material/Add';
import { useCallback, useEffect, useMemo } from 'react';
import { defaultProcedureCodeFilters } from '../components/procedure-codes/procedure-code-filter-bar';
import { ProcedureCodeForm } from '../components/procedure-codes/procedure-code-form/form';
import { ValidatedProcedureCodeFormValues } from '../components/procedure-codes/procedure-code-form/validation-schema';
import { ProcedureCodeGrid } from '../components/procedure-codes/procedure-code-grid';
import {
  DynamicDrawerProvider,
  useDynamicDrawerContext,
} from '../context/dynamic-drawer.context';
import { useSnackbarContext } from '../context/snackbar.context';
import { useBillingWriteAccess } from '../hooks/use-billing-write-access';
import { useFilterUrlSync } from '../hooks/use-filter-url-sync';
import {
  useCreateProcedureCodeMutation,
  useGetAllClientsQuery,
  useGetAllInsurersQuery,
  useGetPartnershipsQuery,
  useGetPaymentTypesQuery,
} from '../types';

export const PROCEDURE_CODES_TEST_ID = 'procedure-codes-container';

const ProcedureCodesContent = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();

  const [filterParams, setFilterParams] = useFilterUrlSync(
    defaultProcedureCodeFilters,
  );

  const { data: insurersData, loading: insurersLoading } =
    useGetAllInsurersQuery();
  const { data: partnershipsData, loading: partnershipsLoading } =
    useGetPartnershipsQuery({ variables: { type: 'billing' } });
  const { data: clientsData, loading: clientsLoading } =
    useGetAllClientsQuery();
  const { data: paymentTypesData, loading: paymentTypesLoading } =
    useGetPaymentTypesQuery();

  const insurers = useMemo(
    () => insurersData?.getAllInsurers ?? [],
    [insurersData],
  );
  const partnerships = useMemo(
    () => partnershipsData?.getPartnerships ?? [],
    [partnershipsData],
  );
  const clients = useMemo(
    () => clientsData?.getAllClients ?? [],
    [clientsData],
  );
  const paymentTypes = useMemo(
    () => paymentTypesData?.getPaymentTypes ?? [],
    [paymentTypesData],
  );

  const [createProcedureCode, { data: createData, error: createError }] =
    useCreateProcedureCodeMutation();

  useEffect(() => {
    if (!createError) {
      return;
    }
    setSnackbarState('Error creating CPT code', 'error', 3000);
  }, [createError, setSnackbarState]);

  useEffect(() => {
    if (!createData) {
      return;
    }
    setSnackbarState('CPT code created successfully', 'success', 3000);
    onClose();
  }, [createData, setSnackbarState, onClose]);

  const handleCreate = useCallback(
    async (values: ValidatedProcedureCodeFormValues): Promise<void> => {
      await createProcedureCode({
        variables: {
          procedureCodeInput: {
            insurerId: values.insurerId,
            cptCode: values.cptCode,
            paymentTypeId: values.paymentTypeId,
            partnershipId: values.partnershipId,
            clientId: values.clientId,
            modifier1: values.modifier1 || null,
            modifier2: values.modifier2 || null,
          },
        },
        refetchQueries: ['getProcedureCodes'],
      });
    },
    [createProcedureCode],
  );

  const handleAddRecord = useCallback((): void => {
    setDrawerContent(
      <HHStack padding={4}>
        <ProcedureCodeForm
          onSubmit={handleCreate}
          insurers={insurers}
          insurersLoading={insurersLoading}
          partnerships={partnerships}
          partnershipsLoading={partnershipsLoading}
          clients={clients}
          clientsLoading={clientsLoading}
          paymentTypes={paymentTypes}
          paymentTypesLoading={paymentTypesLoading}
        />
      </HHStack>,
      <HHTypography hhVariant="h6">Add CPT Code</HHTypography>,
      undefined,
      'temporary',
    );
  }, [
    setDrawerContent,
    handleCreate,
    insurers,
    insurersLoading,
    partnerships,
    partnershipsLoading,
    clients,
    clientsLoading,
    paymentTypes,
    paymentTypesLoading,
  ]);

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={4}
      minHeight="0"
      data-testid={PROCEDURE_CODES_TEST_ID}
    >
      <HHStack
        sx={{ paddingLeft: 6, paddingRight: 6, paddingTop: 6 }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <HHTypography hhVariant="section-title">CPT Codes</HHTypography>
        {canWrite && (
          <HHButton
            hhVariant="variant-bypass"
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={handleAddRecord}
            data-testid="add-procedure-code-button"
          >
            Add New Code
          </HHButton>
        )}
      </HHStack>
      <HHStack sx={{ paddingLeft: 6, paddingRight: 6 }}>
        <HHTooltip
          hhVariant="bottom"
          title={
            <>
              When multiple CPT codes match, the most specific combination wins:
              <br />
              1. Client — exact client match
              <br />
              2. Client + Insurer — client narrowed by insurer
              <br />
              3. Partnership + Insurer — partnership narrowed by insurer
              <br />
              4. Partnership — all clients in a partnership
              <br />
              5. Insurer — broadest match
            </>
          }
        >
          <div style={{ width: '100%' }}>
            <HHAlert
              hhVariant="outlined"
              severity="info"
              sx={{ width: '100%', cursor: 'pointer' }}
            >
              <HHTypography hhVariant="body">
                <strong>Resolution priority (most specific wins):</strong>{' '}
                Client &gt; Client + Insurer &gt; Partnership + Insurer &gt;
                Partnership &gt; Insurer
              </HHTypography>
            </HHAlert>
          </div>
        </HHTooltip>
      </HHStack>
      <ProcedureCodeGrid
        filterParams={filterParams}
        setFilterParams={setFilterParams}
        insurers={insurers}
        insurersLoading={insurersLoading}
        partnerships={partnerships}
        partnershipsLoading={partnershipsLoading}
        clients={clients}
        clientsLoading={clientsLoading}
        paymentTypes={paymentTypes}
        paymentTypesLoading={paymentTypesLoading}
      />
    </HHStack>
  );
};

const ProcedureCodes = (): JSX.Element => (
  <DynamicDrawerProvider>
    <ProcedureCodesContent />
  </DynamicDrawerProvider>
);

export default ProcedureCodes;
